import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Copy, Plus, FolderOpen, Sparkles, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { isOnboardingDone, markOnboardingSkipped } from '../lib/onboarding-storage';

interface Project {
  project_id: string;
  project_type?: string;
  building_type?: string;
  metadata?: { name?: string; address?: string; notes?: string };
  updated_at?: string;
  drawing_proposal?: { status?: string };
}

export function Dashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? 'default';
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', tenantId],
    queryFn: () => api.listProjects(tenantId, { limit: 40 }),
    enabled: !!user?.authenticated,
  });

  const dupMut = useMutation({
    mutationFn: (projectId: string) => api.duplicateProject(projectId, tenantId),
    onSuccess: (rec) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projects/${rec.project_id}/gegevens`);
    },
  });

  const delMut = useMutation({
    mutationFn: (projectId: string) => api.deleteProject(projectId, tenantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  const projects = (data?.projects ?? []) as Project[];
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const showOnboardingBanner = !isOnboardingDone() && !bannerDismissed;

  return (
    <div>
      <SeoMeta
        path="/dashboard"
        noindex
        title="Dashboard"
        description="Jouw CalqTool™ dashboard: overzicht van calculatiedossiers, snelstart nieuw project en toegang tot bibliotheek en account — alleen voor ingelogde gebruikers."
      />
      <AnimatedSection delay={0.1}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Dashboard' }]} />
        <h1 className="mb-2 text-2xl font-bold text-text">Dashboard</h1>
        <p className="mb-6 text-text-muted">
          Welkom — hier start of hervat je je werk met calculatiedossiers en begrotingsposten.
        </p>
      </AnimatedSection>

      {showOnboardingBanner && (
        <AnimatedSection delay={0.12}>
          <div className="relative mb-6 overflow-hidden rounded-xl border border-primary/25 bg-gradient-to-br from-primary/10 via-surface-card to-surface-card p-5 shadow-sm md:p-6">
            <button
              type="button"
              onClick={() => {
                markOnboardingSkipped();
                setBannerDismissed(true);
              }}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-text-muted hover:bg-surface hover:text-text"
              aria-label="Intro-banner sluiten"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex flex-col gap-4 pr-8 md:flex-row md:items-center md:justify-between md:pr-10">
              <div className="flex gap-4">
                <div className="hidden shrink-0 rounded-xl bg-primary/15 p-3 text-primary sm:block">
                  <Sparkles className="h-8 w-8" aria-hidden />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text">Eerste keer hier? Doorloop de intro (±3 min.)</h2>
                  <p className="mt-1 text-sm text-text-muted">
                    Zelfde verhaal als op de landingspagina: Gegevens → Posten → Resultaat → Rapportage, plus optioneel
                    tekening/PDF (upload of uit documentenbibliotheek, importeren mag in delen) — in ±3 minuten.
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
                <Link
                  to="/onboarding"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-alt"
                >
                  Start introductie
                  <Sparkles className="h-4 w-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    markOnboardingSkipped();
                    setBannerDismissed(true);
                  }}
                  className="text-center text-sm font-medium text-text-muted underline-offset-2 hover:text-text hover:underline"
                >
                  Niet nu
                </button>
              </div>
            </div>
          </div>
        </AnimatedSection>
      )}

      <AnimatedSection delay={0.2} stagger={0.08}>
        <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2" data-animate>
          <div className="rounded-lg border border-border bg-surface-card p-5 text-left transition-all duration-200 hover:shadow-md">
            <div className="mb-3 flex items-start gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <Plus className="h-6 w-6 text-primary" />
              </div>
              <div>
                <strong className="text-text">Nieuw dossier starten</strong>
                <p className="mt-1 text-sm text-text-muted">
                  Kies begeleid (vragen) of handmatig. Beide eindigen in hetzelfde dossier met tabs Gegevens, Posten,
                  Resultaat en Rapportage.
                </p>
              </div>
            </div>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              <Link
                to="/projects/intake"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt"
              >
                Begeleid starten (vragen)
              </Link>
              <Link
                to="/projects/new"
                className="inline-flex items-center justify-center rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text hover:bg-surface-card"
              >
                Handmatig starten
              </Link>
            </div>
          </div>

          <div data-animate className="rounded-lg border border-border bg-surface-card p-5 transition-all duration-200 hover:shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 font-semibold text-text">
              <FolderOpen className="h-5 w-5" aria-hidden />
              Recente dossiers
            </h2>
            {isLoading && <p className="text-sm text-text-muted">Laden...</p>}
            {error && <p className="text-sm text-red-600">Kon dossiers niet laden. Probeer later opnieuw.</p>}
            {!isLoading && !error && projects.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-6" role="status" aria-live="polite">
                <img
                  src="/assets/empty-projects.png"
                  alt="Illustratie: nog geen dossiers — start een nieuw bouwproject"
                  className="max-h-32 w-auto object-contain opacity-90"
                  width={280}
                  height={180}
                  decoding="async"
                />
                <p className="text-center text-sm text-text-muted">
                  Nog geen dossiers. Start met <Link to="/onboarding" className="font-medium text-primary hover:underline">introductie</Link>,{' '}
                  <Link to="/projects/intake" className="font-medium text-primary hover:underline">
                    begeleid nieuw dossier
                  </Link>{' '}
                  of{' '}
                  <Link to="/projects/new" className="font-medium text-primary hover:underline">handmatig nieuw dossier</Link>
                  .
                </p>
              </div>
            )}
            {!isLoading && !error && projects.length > 0 && (
              <>
                <p className="mb-2 text-xs text-text-muted">
                  Maximaal 12 dossiers getoond (meest recent eerst). Open tekening-voorstellen hebben een badge.
                </p>
                <ul className="space-y-2">
                  {projects.slice(0, 12).map((p) => (
                    <li key={p.project_id}>
                      <div className="flex flex-wrap items-center gap-1 rounded px-1 py-0.5 hover:bg-surface">
                        <Link
                          to={`/projects/${p.project_id}/gegevens`}
                          className="flex flex-1 flex-wrap items-center gap-2 px-1 py-1 text-sm text-text hover:text-primary"
                        >
                          <span>
                            {p.metadata?.name || p.project_id}
                            {p.updated_at && (
                              <span className="ml-2 text-text-muted">
                                {new Date(p.updated_at).toLocaleDateString('nl-NL')}
                              </span>
                            )}
                          </span>
                          {p.drawing_proposal?.status === 'draft' && (
                            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                              Voorstel te keuren
                            </span>
                          )}
                        </Link>
                        <button
                          type="button"
                          title="Dossier dupliceren"
                          disabled={dupMut.isPending || delMut.isPending}
                          onClick={() => dupMut.mutate(p.project_id)}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-text-muted hover:border-primary hover:text-primary disabled:opacity-50"
                        >
                          <Copy className="h-3.5 w-3.5" aria-hidden />
                          Kopie
                        </button>
                        <button
                          type="button"
                          title="Dossier verwijderen"
                          disabled={delMut.isPending || dupMut.isPending}
                          onClick={() => {
                            const name = p.metadata?.name || p.project_id;
                            if (
                              !globalThis.confirm(
                                `Dossier “${name}” permanent verwijderen? Dit kan niet ongedaan worden gemaakt.`
                              )
                            )
                              return;
                            delMut.mutate(p.project_id);
                          }}
                          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          Verwijderen
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
}
